import { QueryClient } from '@tanstack/react-query'

/**
 * The app's single QueryClient.
 *
 * Lifted out of `App.jsx` so non-React code — specifically the auth store —
 * can reach it. It must be importable WITHOUT pulling in the store, or the two
 * modules would form an import cycle; this file therefore depends on nothing
 * but React Query.
 *
 * Why the store needs it: the cache outlives a session. One client is created
 * per page load, and query keys are scoped to the RESOURCE, not the viewer
 * (`['spaces', workspaceId]`, `['workspaces']`, `['space', id]`). Signing out
 * used to clear the user, tenant and socket but leave every cached response
 * behind, so the next person to sign in on that tab got a cache HIT on the
 * previous person's data — including Spaces they have no rights to — and
 * `staleTime` being unset means React Query hands that stale value straight to
 * the render while it refetches. `refetchOnWindowFocus: false` removes the
 * usual self-correction, so it persisted until a manual reload (which is why a
 * browser refresh "fixed" it: a fresh page = a fresh, empty client).
 *
 * `clearQueryCache()` is the one hook the store calls at every session
 * boundary. Keep this file free of app imports so that stays true.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

/** Drop every cached query. Call on any change of identity — sign-in,
 *  sign-out, account switch — so no response outlives the session that was
 *  authorised to see it. */
export function clearQueryCache() {
  queryClient.clear()
}
